"use client";

import React from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const narrativeBlocks = [{
  type: "text",
  content: "The M / O Protocol was born from a realization that modern healthcare often overlooks the most vital element: the individual's unique physiological rhythm. Transitioning from MO Selfcare to M / O Protocol represents our evolution from a wellness boutique into a clinical standard for longevity and cellular optimization."
}, {
  type: "quote",
  content: "We are redefining the intersection of medical precision and holistic tranquility."
}, {
  type: "text",
  content: "Our philosophy is rooted in the 'Protocol'—a systematic, data-driven approach to wellness that utilizes advanced IV therapies, lab diagnostics, and regenerative treatments. We don't just treat symptoms; we curate environments where your body can perform its own restorative miracles."
}, {
  type: "quote",
  content: "true luxury is the peace of mind that comes from knowing your health is in expert hands."
}, {
  type: "text",
  content: "By integrating clinical excellence with a high-end aesthetic, we provide a sanctuary for those in Midland and Odessa who demand more from their healthcare. This is the new standard of care—sophisticated, intentional, and entirely focused on your long-term vitality."
}];
function NarrativeBlock({
  block,
  index
}: {
  block: any;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5,
    once: false
  });
  return <div data-index={index} ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {block.type === "quote" ? <blockquote className="my-12 border-l-4 border-[#8FA88F] pl-6 py-2">
          <p className="text-3xl md:text-4xl lg:text-5xl font-serif text-primary leading-tight tracking-tight">
            "{block.content}"
          </p>
        </blockquote> : <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-sans">
          {block.content}
        </p>}
    </div>;
}
export default function KitchenOrigins() {
  return <section data-section-id="223949" id="the-m-o-protocol-philosophy" className="bg-card text-foreground py-24 md:py-32 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start lg:h-[800px]">
          
          {}
          <div className="relative h-[500px] lg:h-full w-full rounded-2xl bg-background/50 border border-border/30 p-6 md:p-10 shadow-sm">
            {}
            <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-card to-transparent z-10 pointer-events-none rounded-t-2xl" />
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-card to-transparent z-10 pointer-events-none rounded-b-2xl" />
            
            <ScrollArea className="h-full pr-6">
              <div className="space-y-12 py-12">
                <div className="mb-16">
                  <h2 className="text-sm font-bold tracking-[var(--tracking-brand-widest)] text-[#8FA88F] uppercase mb-4 font-sans">
                    Evolution of Care
                  </h2>
                  <h3 className="text-4xl md:text-6xl font-normal font-default tracking-tight text-foreground">
                    The M / O Protocol Philosophy
                  </h3>
                </div>

                {narrativeBlocks.map((block, i) => <NarrativeBlock key={i} block={block} index={i} />)}
                
                {}
                <div className="h-32" />
              </div>
            </ScrollArea>
          </div>

          {}
          <div className="relative h-[600px] lg:h-full w-full hidden md:block">
            <div className="absolute inset-0 w-full h-full">
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/clinical_detail_soft_1.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/clinical_detail_soft_1.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/clinical_detail_soft_1.png-wvc-sizes" alt="Premium clinical detail" loading="lazy" className="absolute top-0 left-0 w-[65%] h-[55%] object-cover rounded-tl-4xl rounded-br-4xl grayscale opacity-40 shadow-md transition-transform duration-1000 hover:scale-105" />
              
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/wellness_sanctuary_2.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/wellness_sanctuary_2.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/wellness_sanctuary_2.png-wvc-sizes" alt="Luxury wellness environment" loading="lazy" className="absolute bottom-0 right-0 w-[70%] h-[60%] object-cover rounded-tl-4xl rounded-br-4xl grayscale opacity-30 shadow-md transition-transform duration-1000 hover:scale-105" />
              
              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[50%] z-20">
                <div className="w-full h-full relative rounded-tl-4xl rounded-br-4xl overflow-hidden shadow-xl border-8 border-background ring-1 ring-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/iv_therapy_detail_color.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/iv_therapy_detail_color.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/iv_therapy_detail_color.png-wvc-sizes" alt="M / O Protocol IV Therapy Detail" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
                  {}
                  <div className="absolute inset-0 shadow-[inset_0_0_50px_rgba(0,0,0,0.05)] pointer-events-none mix-blend-overlay" />
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="md:hidden flex flex-col gap-6 mt-8">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/iv_therapy_detail_color.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/iv_therapy_detail_color.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/iv_therapy_detail_color.png-wvc-sizes" alt="M / O Protocol IV Therapy Detail" loading="lazy" className="w-full h-64 object-cover rounded-tl-3xl rounded-br-3xl shadow-md border-4 border-background" />
            <div className="grid grid-cols-2 gap-4">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/clinical_detail_soft_1.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/clinical_detail_soft_1.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/clinical_detail_soft_1.png-wvc-sizes" alt="Premium clinical detail" loading="lazy" className="w-full h-40 object-cover rounded-tl-2xl rounded-br-2xl grayscale opacity-80" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/wellness_sanctuary_2.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/wellness_sanctuary_2.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/wellness_sanctuary_2.png-wvc-sizes" alt="Luxury wellness environment" loading="lazy" className="w-full h-40 object-cover rounded-tl-2xl rounded-br-2xl grayscale opacity-80" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}